import { memo, useMemo, useRef, useState } from 'react';
import BodyContentSidebar from 'components/BodyContentSidebar';
import EntitiesSidebar from 'components/EntitiesSidebar';
import ErrorBoundary from 'error-handler/ErrorBoundary';
import KnowledgeGraphContainer from 'components/knowledgeGraph/KnowledgeGraphContainer';
import MessageComposer from 'components/message/MessageComposer';
import Tutorial from 'components/tutorial/Tutorial';
import Chat from 'components/chat/Chat';
import Settings from 'components/settings/Settings';
import Discovery from 'components/discovery/Discovery';
import CustomerSelections from 'components/customers/selections/CustomerSelections';
import QuestionTests from 'components/question-tests/QuestionTests';

import clsx from 'clsx';

import DiscoveryModal from './DiscoveryModal';
import KnowledgeGraphModal from './KnowledgeGraphModal';

import { currentOpenModalStates } from 'config/constants';

import { ChatActiveContext } from 'components/chat/ChatActiveContext';

import useMediaQuery from 'utils/mediaQueries';
import { useSnackbar } from 'notistack';

import { KnowledgeGraphSidebarDrawerWidth } from 'components/sidebar/KnowledgeGraphSidebar/KnowledgeGraphSidebar';

import { setEntitiesSidebar } from 'store/modules/graph/graph';

import withEmbedded from 'root/hocs/withEmbedded';

import { connect } from 'react-redux';

import styles from './home.scss';

// The button should be displayed if the user has scrolled up at least this number of pixels
const minimumScrollForButton = 100;

const PageContent = ({
  isEmbedded,
  graph,
  chatEntryPoint,
  showVisualGraph,
  showKgSidebar,
  showChat,
  showChatComponent,
  showSettings,
  showTests,
  showDiscovery,
  selectedDiscoveryTopic,
  isOpenCustomerSelectionRoute,
  visualGraphHasRendered,
  displayModal,
  dispatch
}) => {
  const [shouldScrollAutomatically, setShouldScrollAutomatically] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  const isMobile = useMediaQuery();
  const { enqueueSnackbar } = useSnackbar();

  const scrollTimeout = useRef(null);
  const containerRef = useRef(null);

  const closeEntitiesSidebar = () => dispatch(setEntitiesSidebar({ isOpen: false }));

  const adjustPaddingForEmbeddedSidebar =
    isEmbedded && showKgSidebar ? { paddingLeft: KnowledgeGraphSidebarDrawerWidth + 'px' } : { paddingLeft: 0 };

  const component = useMemo(() => {
    if (showSettings) {
      return (
        <div className={styles.bodyRight}>
          <Settings />
        </div>
      );
    } else if (showTests) {
      return (
        <div className={styles.bodyRight}>
          <div className={styles.tests}>
            <QuestionTests />
          </div>
        </div>
      );
    } else if (showDiscovery) {
      return <Discovery selectedTopicIndex={selectedDiscoveryTopic} isMobile={isMobile} />;
    } else if (showChat && isOpenCustomerSelectionRoute) {
      return (
        <div className={styles.customerSelections}>
          <CustomerSelections />
        </div>
      );
    }
  }, [
    showSettings,
    showTests,
    showDiscovery,
    selectedDiscoveryTopic,
    isOpenCustomerSelectionRoute,
    showChat,
    isMobile
  ]);

  const handleScroll = () => {
    const shouldScrollAutomatically =
      containerRef.current.scrollHeight - containerRef.current.clientHeight >
      containerRef.current.scrollTop + minimumScrollForButton;

    setShouldScrollAutomatically(shouldScrollAutomatically);

    if (!isScrolling) {
      setIsScrolling(true);
    }

    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    // Consider scroll has ended after 200ms of inactivity
    scrollTimeout.current = setTimeout(() => {
      setIsScrolling(false);
    }, 200);
  };

  return (
    <div className={styles.paneBodyRight}>
      {!isEmbedded && (
        <BodyContentSidebar open={graph.entitiesSidebar.isOpen} onClose={closeEntitiesSidebar}>
          <EntitiesSidebar />
        </BodyContentSidebar>
      )}
      {/* A key allows the error to be removed if the component changes. */}
      {component && <ErrorBoundary key={component}>{component}</ErrorBoundary>}
      {/*
            After having rendered the KG component for the first time (when its graph is correctly positioned / laid out)
            we keep it in the DOM and show / hide it. This way:
              - the KG component's loader is only shown once
              - modifications to the graph (move / zoom) persist when switching to the chat and back
          */}
      {visualGraphHasRendered && (
        <div
          className={styles.chat}
          style={{
            // ...and show / hide it
            display: showVisualGraph ? 'block' : 'none',
            ...adjustPaddingForEmbeddedSidebar
          }}
        >
          <ErrorBoundary>
            <KnowledgeGraphContainer graph={graph} isHidden={!showVisualGraph} />;
          </ErrorBoundary>
        </div>
      )}
      {/* IMPORTANT! Chat Component should be always in DOM because of performance problems */}
      <ChatActiveContext.Provider value={showChatComponent}>
        <div className={`${styles.chatContainer} ${showChatComponent && !showVisualGraph ? null : styles.hideChat}`}>
          <div
            className={styles.chat}
            ref={containerRef}
            onScroll={handleScroll}
            style={adjustPaddingForEmbeddedSidebar}
            id="ChatComponentHook"
          >
            <ErrorBoundary>
              <Chat
                isVisible={showChatComponent}
                containerRef={containerRef}
                isScrolling={isScrolling}
                shouldScrollAutomatically={shouldScrollAutomatically}
                chatEntryPoint={chatEntryPoint}
                isMobile={isMobile}
                enqueueSnackbar={enqueueSnackbar}
              />
            </ErrorBoundary>
          </div>
        </div>

        <div
          className={`${styles.messageComposer} ${showChatComponent ? null : styles.hideChat}`}
          style={adjustPaddingForEmbeddedSidebar}
        >
          <MessageComposer />
          <Tutorial />
        </div>
      </ChatActiveContext.Provider>
      {displayModal === currentOpenModalStates.discovery && (
        <div className={styles.modalContainer}>
          <DiscoveryModal />
        </div>
      )}
      {isEmbedded && displayModal === currentOpenModalStates.knowledgeGraph && (
        <div className={clsx(styles.modalContainer, styles.knowledgeGraphModal)}>
          <KnowledgeGraphModal />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  graph: state.graph,
  isOpenCustomerSelectionRoute: state.customerSelections.isOpenCustomerSelectionRoute
});

export default connect(mapStateToProps)(withEmbedded(memo(PageContent)));
